import React, { useEffect, type FC, useRef } from 'react'
import { Platform, Animated, Easing } from 'react-native'
import { useComponentStyle } from '../../../themes'
import type {
  ComponentThemeFactory,
  ComponentThemeProps,
} from '../../../themes/util/types'
import type { SpinnerTheme } from '.'
import { colors, baseColors } from '../../../themes/techapp-light'
declare module '../../../themes/util/types' {
  interface ComponentThemes {
    Spinner: SpinnerTheme
  }
}

const spinnerSize = 22
const baseSpinnerTheme: ComponentThemeFactory<SpinnerTheme> = ({ colors }) => ({
  spinner: [
    {
      style: {
        borderColor: colors.screen,
        borderTopColor: colors.primary,
        borderRadius: spinnerSize * (parseFloat('50%') / 100),
        borderWidth: 4,
        borderRightColor: colors.primary,
        borderBottomColor: colors.primary,
        height: spinnerSize,
        width: spinnerSize,
      },
    },
  ],
})
type OpaqueColorValue = symbol & { __TYPE__: 'Color' }
type ColorValue = string | OpaqueColorValue
type SpinnerProps = ComponentThemeProps<SpinnerTheme> & {
  slow?: boolean
  spinnerActiveColor?: ColorValue
  spinnerBorderColor?: ColorValue
}

const Spinner: FC<SpinnerProps> = ({
  decoration,
  styles = {},
  layout,
  slow = false,
  spinnerActiveColor = colors.primary,
  spinnerBorderColor = baseColors.white,
}) => {
  const spinnerStyles = useComponentStyle(
    'Spinner',
    baseSpinnerTheme,
    styles,
    layout,
    decoration,
  )
  const spinValue = useRef(new Animated.Value(0))

  useEffect(() => {
    Animated.loop(
      Animated.timing(spinValue.current, {
        toValue: 1,
        duration: slow ? 20000 : 1000,
        easing: Easing.linear,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ).start()
  }, [slow])

  const spin = spinValue.current.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  })

  return (
    <Animated.View
      testID="spinner"
      role="alert"
      style={[
        spinnerStyles.spinner,
        {
          transform: [{ rotate: spin }],
          borderColor: spinnerBorderColor,
          borderTopColor: spinnerActiveColor,
          borderRightColor: spinnerActiveColor,
          borderBottomColor: spinnerActiveColor,
        },
      ]}
    ></Animated.View>
  )
}

export default Spinner
